/**
 * Persists the most recently selected Agent (model) connection so that newly
 * added Agent nodes can prefer a connection the user actually uses. Stored in
 * localStorage so the preference survives page reloads and crosses workflows
 * within the same browser profile.
 */

const STORAGE_KEY = 'otto:last-used-agent-connection';

export interface LastUsedAgentConnection {
	/** Workflow-scoped reference key (e.g. `azureopenai-1`). */
	key: string;
	/** Cross-workflow stable connection id (e.g. ARM resource id, or local connection name). */
	connectionId?: string;
	/** Epoch ms when the selection was recorded. */
	ts: number;
}

export const getLastUsedAgentConnection = (): LastUsedAgentConnection | undefined => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return undefined;
		const parsed = JSON.parse(raw) as Partial<LastUsedAgentConnection>;
		if (!parsed || typeof parsed.key !== 'string' || !parsed.key) return undefined;
		const result: LastUsedAgentConnection = {
			key: parsed.key,
			ts: typeof parsed.ts === 'number' ? parsed.ts : 0,
		};
		if (typeof parsed.connectionId === 'string' && parsed.connectionId) {
			result.connectionId = parsed.connectionId;
		}
		return result;
	} catch {
		return undefined;
	}
};

export const recordAgentConnectionUsage = (key: string, connectionId?: string): void => {
	if (!key) return;
	try {
		const payload: LastUsedAgentConnection = {
			key,
			ts: Date.now(),
		};
		if (connectionId) {
			payload.connectionId = connectionId;
		}
		localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
	} catch {
		/* localStorage may be unavailable (private mode, SSR) — silently ignore */
	}
};

/** Test-only escape hatch; clears the stored preference. */
export const clearLastUsedAgentConnection = (): void => {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		/* noop */
	}
};
